import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TimeRange } from "../../components/monitoring-dashboard/time-range-picker/TimeRangePicker";
import { apiClient } from "../../lib/api";
import { LogTextFilter } from "../../interfaces/LogQuery";

export type VisualizationType =
  | "timeseries"
  | "logs"
  | "gauge"
  | "stat"
  | "image"
  | "hostmap"
  | "3d"
  | "map"
  | "piechart"
  | "barchart"
  | "traces";
export type LegendPosition = "top" | "left" | "bottom" | "right";

export interface PanelConfig {
  // Timeseries options
  showGrid?: boolean;
  yAxisLabel?: string;
  yAxisMin?: number | null;
  yAxisMax?: number | null;
  lineTension?: number;
  showPoints?: boolean;
  fillArea?: boolean;
  tooltipMode?: string;
  showLegend?: boolean;
  legendPosition?: LegendPosition;
  // Gauge/Stat common options
  min?: number | null;
  max?: number | null;
  unit?: string; // e.g. "°C", "%", "ms"
  decimals?: number; // formatting
  // Gauge specific
  startAngle?: number; // default 225
  endAngle?: number; // default -45
  showProgress?: boolean;
  // New gauge appearance options
  showLabels?: boolean;
  gaugeRingWidth?: number; // axisLine width
  gaugeProgressWidth?: number; // progress width
  thresholds?: { value: number; color: string }[]; // ascending by value
  // NEW: font size for the numeric value in Gauge center
  gaugeValueFontSize?: number; // px
  // Font size of the gauge min/max and threshold labels; auto when unset
  gaugeLabelFontSize?: number; // px
  // Show threshold labels on gauge
  showThresholdLabels?: boolean;
  // Draw the threshold colours as a ring around the gauge bar (default true)
  showThresholdMarkers?: boolean;
  // Stat specific
  showSparkline?: boolean;
  statFontSize?: number; // px
  // Where the stat takes its colour from (default "value")
  statColorMode?: "none" | "value" | "background";
  // Whether the stat draws its history (defaults to showSparkline)
  statGraphMode?: "none" | "area";
  // Horizontal placement of the value; "auto" is the legacy name for centred
  statJustifyMode?: "auto" | "center" | "left" | "right";
  // Gauge/Stat multi-value grid: fixed tiles per row, auto when unset
  tileColumns?: number;
  // NEW: Staleness handling for gauge/stat
  hideStaleValues?: boolean; // when false, always show last value
  staleMaxMs?: number | null; // when set (>0), max allowed age in ms; when null/undefined -> auto
  // HostMap specific
  hostmapTileSize?: number; // px size for hex tiles
  hostmapShowLabels?: boolean; // show device label over tile
  hostmapShowClusterLabels?: boolean; // show cluster label
  hostmapShowLegend?: boolean; // show legend
  hostmapMaxHexPerRow?: number; // max hexagons per row
  // 3D scene options
  /** Shape drawn by the panel: "path" | "vector" | "statusRing" | "bar" */
  threeDShape?: string;
  /** Shape-specific property bag, validated against the shape catalog. */
  threeDShapeProps?: Record<string, unknown>;
  /** Source coordinate convention; "z" (ROS) is converted to the viewer frame. */
  threeDUpAxis?: "y" | "z";
  threeDShowGrid?: boolean;
  threeDShowAxes?: boolean;
  threeDGridSize?: number;
  threeDGridDivisions?: number;
  /** Camera placement saved with the panel; replaces the automatic framing. */
  threeDViewport?: {
    position: [number, number, number];
    target: [number, number, number];
  };
  /** Static map (occupancy grid) rendered as a ground plane. */
  threeDMap?: {
    id: string;
    opacity?: number;
  };
  /** @deprecated superseded by threeDShapeProps.drawStyle */
  threeDMode?: "line" | "points";
  /** @deprecated superseded by threeDShapeProps.showLatestMarker */
  threeDShowLatestMarker?: boolean;
  // Map options
  mapInitialZoom?: number;
  mapDefaultCenter?: string; // "lat,lon"
  mapRenderMode?: "latest" | "path";
  mapShowLabels?: boolean;
  mapShowPoints?: boolean;
  mapPathColor?: string;
  mapPointColor?: string;
  // Pie Chart specific
  pieChartMode?: "pie" | "doughnut"; // pie or doughnut (ring)
  pieChartLabelType?:
    | "label"
    | "value"
    | "percentage"
    | "label-value"
    | "label-percentage"; // what to show on slices
  pieChartShowLegend?: boolean; // show legend
  pieChartLegendPosition?: "top" | "bottom" | "left" | "right";
  pieChartRadius?: number; // outer radius % (0-100)
  pieChartInnerRadius?: number; // inner radius % for doughnut (0-100)
  pieChartShowLabels?: boolean; // show labels on slices
  pieChartShowValues?: boolean; // show values in center or tooltip
  pieChartColorScheme?: "default" | "pastel" | "vibrant" | "monochrome"; // color scheme
  pieChartCustomColors?: string[]; // custom color array
  // Bar Chart specific
  barChartOrientation?: "vertical" | "horizontal"; // bar orientation
  barChartStacked?: boolean; // stack bars for multiple series
  barChartShowValues?: boolean; // show values on bars
  barChartBarWidth?: number; // bar width % (0-100)
  barChartShowLegend?: boolean; // show legend
  barChartLegendPosition?: "top" | "bottom" | "left" | "right";
  barChartColorScheme?: "default" | "pastel" | "vibrant" | "monochrome";
  barChartCustomColors?: string[]; // custom color array
  barChartXAxisLabel?: string; // x-axis label
  barChartYAxisLabel?: string; // y-axis label
  barChartShowGrid?: boolean; // show grid lines
  barChartLabelRotation?: number; // rotate x-axis labels (degrees)
  // Logs options
  wrap?: boolean;
  autoScroll?: boolean;
  showLevel?: boolean;
}

// Generic query builder types
export interface TagFilter {
  key: string;
  operator: string; // Only '=' or '!=' supported
  value: string;
}

export interface GroupBy {
  time?: string; // e.g. "auto", "1h", "5m", "1d" - time resolution/bucket for aggregation
  field?: string; // e.g. "device:name", "device:site", "tag:model"
}

export interface GenericQueryRequest {
  site?: string;
  device?: string;
  topic?: string;
  field: string;
  tagFilters?: TagFilter[];
  logTextFilters?: LogTextFilter[];
  /** How multiple tagFilters / logTextFilters are combined. Defaults to "AND". */
  filterLogic?: "AND" | "OR";
  log?: string;
  // functions?: string[];
  aggregation: string;
  groupBy?: GroupBy;
  aliasFn?: string;
  // Array element selection (ros_msgs tables only).
  // arrayMode: "" | "index" | "all"
  arrayMode?: string;
  arrayIndex?: number; // 0-based index when arrayMode=="index"
  arrayAggMode?: string; // "split" (default) | "aggregate" when arrayMode=="all"
  /**
   * 3D panel only: which data slot of the selected shape this query feeds
   * (e.g. "x", "y", "z", "value"). Frontend-only, ignored by the backend.
   */
  threeDSlot?: string;
}

// *** Updated DashboardPanel interface with new timeseries options ***
export interface DashboardPanel {
  id: string;
  dashboardId?: string; // Optional to maintain backward compatibility
  title: string;
  metricIds: string[];
  visualizationType: VisualizationType;
  config: PanelConfig;
  // New: optional generic queries
  queries?: GenericQueryRequest[];
}

// --- Define State Shape ---
interface MonitoringDashboardState {
  config: DashboardConfig;
}

export interface DashboardConfig {
  timeRange: TimeRange;
  refreshIntervalMs: number;
}

export const updateDashboardConfig = createAsyncThunk<
  Partial<DashboardConfig>,
  { config: Partial<DashboardConfig>; dashboardId: string },
  { rejectValue: string }
>(
  "monitoringDashboard/updateDashboardConfig",
  async ({ config, dashboardId }, { rejectWithValue }) => {
    try {
      await apiClient.put<DashboardConfig>(
        `/monitoring/${dashboardId}/config`,
        config,
      );
      return config;
    } catch (error) {
      console.error("Failed to update dashboard config:", error);
      return rejectWithValue("Failed to update dashboard config");
    }
  },
);

// --- Initial State ---
const initialState: MonitoringDashboardState = {
  config: {
    timeRange: { type: "relative", value: 15 * 60 * 1000 },
    refreshIntervalMs: 60 * 1000,
  },
};

// --- Slice Definition ---
const monitoringDashboardSlice = createSlice({
  name: "monitoringDashboard",
  initialState,
  reducers: {
    setDashboardConfig: (state, action: PayloadAction<DashboardConfig>) => {
      state.config = action.payload;
    },
    updateTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.config.timeRange = action.payload;
    },
    updateRefreshInterval: (state, action: PayloadAction<number>) => {
      state.config.refreshIntervalMs = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      updateDashboardConfig.fulfilled,
      (state, action: PayloadAction<Partial<DashboardConfig>>) => {
        state.config = { ...state.config, ...action.payload };
      },
    );
  },
});

// --- Export Actions ---
export const { setDashboardConfig, updateTimeRange, updateRefreshInterval } =
  monitoringDashboardSlice.actions;

// --- Export Selectors ---
export const selectDashboardConfig = (state: {
  monitoringDashboardReducer: MonitoringDashboardState;
}): DashboardConfig => state.monitoringDashboardReducer.config;

// --- Export the Reducer ---
export const monitoringDashboardReducer = monitoringDashboardSlice.reducer;
